import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import { useStore } from "../lib/store";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const [state, setState] = useState("polling"); // polling | success | expired | error
  const [order, setOrder] = useState(null);
  const { clearCart } = useStore();
  const attempts = useRef(0);

  const poll = useCallback(async () => {
    if (!sessionId) return;
    if (attempts.current >= 8) { setState("error"); return; }
    attempts.current += 1;
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState("success");
        clearCart();
        // Checkout status already returned amount/order_id — try to fetch full order
        // using access token stored via URL (stripe may not preserve; falls back to status data).
        const t = params.get("t");
        const q = t ? `?t=${t}` : "";
        api.get(`/orders/${orderId}${q}`).then((r) => setOrder(r.data)).catch(() => {
          // minimal display from status
          setOrder({ id: data.order_id, total: (data.amount_total || 0) / 100 });
        });
        return;
      }
      if (data.status === "expired") { setState("expired"); return; }
      setTimeout(poll, 2000);
    } catch {
      setState("error");
    }
  }, [sessionId, orderId, clearCart]);

  useEffect(() => { poll(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="container-kv pt-32 pb-20 min-h-[60vh] flex items-center justify-center" data-testid="payment-success-page">
      <div className="card-kv max-w-xl w-full text-center py-12 px-6">
        {state === "polling" && (
          <>
            <Loader2 size={40} className="mx-auto mb-5 text-[#C8A97E] animate-spin" />
            <h1 className="font-serif text-3xl mb-3">Verifying payment...</h1>
            <p className="text-[#A8A5A1]">Please wait while we confirm your transaction.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 size={48} className="mx-auto mb-5 text-[#4ADE80]" />
            <h1 className="font-serif text-3xl mb-3" data-testid="payment-success-heading">Order confirmed</h1>
            <p className="text-[#A8A5A1] mb-6">Thank you for your order. A confirmation has been sent to your email.</p>
            {order && (
              <div className="text-left text-sm space-y-2 mb-6 border-t border-white/5 pt-5">
                <div className="flex justify-between"><span className="text-[#75726D]">Order ID</span><span className="font-mono text-xs">{order.id.slice(0, 8).toUpperCase()}</span></div>
                <div className="flex justify-between"><span className="text-[#75726D]">Total</span><span>${order.total.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-[#75726D]">Status</span><span className="text-[#4ADE80]">Paid</span></div>
              </div>
            )}
            <Link to="/shop" className="btn-gold inline-flex">Continue Shopping</Link>
          </>
        )}
        {state === "expired" && (
          <>
            <XCircle size={40} className="mx-auto mb-5 text-[#FCA5A5]" />
            <h1 className="font-serif text-3xl mb-3">Session expired</h1>
            <p className="text-[#A8A5A1] mb-6">Your checkout session timed out. Please try again.</p>
            <Link to="/checkout" className="btn-gold inline-flex">Retry</Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={40} className="mx-auto mb-5 text-[#FCA5A5]" />
            <h1 className="font-serif text-3xl mb-3">Verification timeout</h1>
            <p className="text-[#A8A5A1] mb-6">We couldn't verify your payment in time. If you were charged, please contact us.</p>
            <Link to="/contact" className="btn-gold inline-flex">Contact Support</Link>
          </>
        )}
      </div>
    </div>
  );
}
